import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Mail, Calendar, RefreshCw, Ticket, MessageCircle, Plus, Search, Filter, EyeOff, Eye, Cog, ChevronDown, ChevronUp, Maximize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import EmailList from './EmailList';
import DashboardTicketList from './DashboardTicketList';
import TeamsMessageList from './TeamsMessageList';
import CompactEventList from './CompactEventList';
import CalendarEventDetail from './CalendarEventDetail';
import TicketDetail from './TicketDetail';
import EmailFilterModal from './EmailFilterModal';
import TeamsFilterModal from './TeamsFilterModal';
import TicketFilterModal from './TicketFilterModal';
import TokenDebug from './TokenDebug';
import ReleaseNoteOverlay, { useReleaseNote } from './dashboard/ReleaseNote';
import useDashboardLayout from './dashboard/useDashboardLayout';
import useFilterSettings from './dashboard/useFilterSettings';
import CreateTicketModal, { useCreateTicket } from './dashboard/CreateTicket';
import useDashboardMails from './dashboard/useDashboardMails';
import useTodayEvents from './dashboard/useTodayEvents';
import useDashboardTickets from './dashboard/useDashboardTickets';
import { sichtbareBereiche, erlaubteBereiche } from './dashboard/bereiche'; // #563, #569
import '../styles/Dashboard.css';
import { useI18n } from '../utils/i18n';
import DayCalendarPanel from './DayCalendarPanel';

import * as loadingDebug from '../utils/loadingDebug';
import useIsMobile from '../hooks/useIsMobile';

const Dashboard = () => {
  // W6 (#16): TicketDetail bekommt auf schmalen Screens das kompakte Mobil-Layout
  const isMobileScreen = useIsMobile();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [, setTeamsMessageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [eventRefreshKey, setEventRefreshKey] = useState(0);
  const todayDate = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // #471: Hinweis bei Login (components/dashboard/ReleaseNote.js). Der Hook
  // steht hier oben, nicht in der Overlay-Komponente: Nur so übersteht der
  // Zustand das frühe return beim Laden und „Erneut versuchen“.
  const { releaseNote, showReleaseNote, handleCloseReleaseNote } = useReleaseNote();

  // #528: Dialog „Neues Ticket“ (components/dashboard/CreateTicket.js). Der
  // Hook steht hier und nicht im Dialog: Nur so übersteht der Entwurf das
  // frühe return beim Laden und „Erneut versuchen“. manualRefreshTickets kommt
  // als Pfeil herein, weil useDashboardTickets erst weiter unten steht.
  const anlegen = useCreateTicket({ manualRefreshTickets: () => manualRefreshTickets(), setEventRefreshKey });
  const { setShowCreateTicketForm, setPendingCreateTicketFiles } = anlegen;

  // #471: Filter-Einstellungen für Mails, Teams und Tickets
  // (components/dashboard/useFilterSettings.js). Das Neuladen der Mails nach
  // dem Speichern kommt als Funktion herein, weil useAutoCache erst weiter
  // unten steht; aufgerufen wird sie erst beim Speichern.
  const {
    showEmailFilterModal, setShowEmailFilterModal, emailFilterSettings, emailFilterLoading,
    showTeamsFilterModal, setShowTeamsFilterModal, teamsFilterSettings, teamsFilterLoading,
    showTicketFilterModal, setShowTicketFilterModal, ticketFilterSettings, ticketFilterLoading,
    handleEmailFilterSave, handleTeamsFilterSave, handleTicketFilterSave
  } = useFilterSettings({ manualRefreshEmails: () => manualRefreshEmails() });

  // Load current user once on component mount
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const userResponse = await apiService.getCurrentUser();
        setCurrentUser(userResponse);

      } catch (err) {
        console.error('Error loading current user:', err);
      }
    };
    loadCurrentUser();
  }, []);

  // #44-Revision 2026-10-19: Den Pool sehen nur Manager. Solange der Benutzer
  // noch lädt, bleibt die Spalte stehen -- sonst springt das Layout für
  // Manager bei jedem Laden; ein Ticketworker bekommt ohnehin keine
  // Pool-Tickets vom Server.
  const siehtPool = !currentUser || currentUser?.dbUser?.isManager === true;

  // Helper to get current user's email (supports different shapes)
  const getCurrentUserEmail = useCallback(() => {
    if (!currentUser) return null;
    return typeof currentUser === 'string' ? currentUser : (currentUser?.userPrincipalName || currentUser?.mail || currentUser?.email);
  }, [currentUser]);

  // #471: Spalten, Heute-Kalender und Breitenmessung
  // (components/dashboard/useDashboardLayout.js).
  const {
    hideEmails, setHideEmails, hideTeams, setHideTeams, showTodayCalendar,
    ausgeblendet, reihenfolge, blendeBereich,
    todayCalendarWrapRef, todayCalendarHeight, ticketsSectionRef, ticketsHeaderRef, isTicketsNarrow,
    saveHiddenColumns, handleActivateTodayCalendar, handleDeactivateTodayCalendar
  } = useDashboardLayout({ currentUser, getCurrentUserEmail, loading });

  // #528: Mails (Cache, SSE, Suche) und heutige Termine
  // (components/dashboard/useDashboardMails.js, useTodayEvents.js). Die Hooks
  // stehen hier und nicht in den Spalten: Nur so überstehen Liste, Abo und
  // Termine das frühe return beim Laden und „Erneut versuchen“.
  const {
    emails, emailsLoading, setEmailsLoading, manualRefreshEmails, emailErrorCount, emailsSuspended, emailsStatus,
    emailSearchFilter, setEmailSearchFilter, emailSearchResults, emailSearchLoading, handleEmailReadUpdate
  } = useDashboardMails();
  const {
    events, setEvents, linkedCalendarEventIds, setLinkedCalendarEventIds,
    calendarEventTicketStatus, setCalendarEventTicketStatus, manualRefreshEvents, eventErrorCount, eventsSuspended
  } = useTodayEvents();

  // #529 (Scheibe 4 aus #313): Ticketlisten samt Filter, Cache, Ereignissen
  // und Abgleich im Hintergrund (components/dashboard/useDashboardTickets.js,
  // useTicketSync.js). Der Hook steht hier und nicht in den Spalten: Nur so
  // ueberstehen Listen, Suchfelder und Unterdrueckung das fruehe return beim
  // Laden und "Erneut versuchen". loadDashboardData befuellt die Listen beim
  // Start selbst, deshalb gehen Setter und Filter mit hinaus.
  const {
    tickets, setTickets, myTickets, setMyTickets, setOriginalTickets,
    ticketSearchFilter, setTicketSearchFilter, myTicketSearchFilter, setMyTicketSearchFilter,
    optimisticHiddenIdsRef, filterTicketsAssignedToMe, filterTicketsNotAssignedToMe,
    manualRefreshTickets, ticketErrorCount, ticketsSuspended, ticketsStatus, plannedTicketEntries
  } = useDashboardTickets({ currentUser, getCurrentUserEmail, ticketFilterSettings });

  // Handle Teams message count updates
  const handleTeamsMessageCountUpdate = useCallback((count) => {
    setTeamsMessageCount(count);
  }, []);

  // #473: Der eine Weg „nach einer Änderung neu laden“ -- Tickets, Termine und
  // die Ticket-Verknüpfungen der Termine, danach die CompactEventList. Stand
  // vorher fünfmal kopiert im Code. Wirft, wenn Tickets oder Termine scheitern;
  // die CompactEventList wird trotzdem angestoßen.
  const aktualisiereNachAenderung = useCallback(async () => {
    try {
      const [, , linked] = await Promise.all([
        manualRefreshTickets(),
        manualRefreshEvents(), // Kalender-Markierungen
        (async () => { try { return await apiService.getLinkedItems(); } catch (_) { return null; } })()
      ]);
      if (linked) {
        setLinkedCalendarEventIds(linked.linkedCalendarEventIds || []);
        setCalendarEventTicketStatus(linked.calendarEventTicketStatus || {});
      }
    } finally {
      setEventRefreshKey(prev => prev + 1);
    }
    // #528: Die beiden Setter kommen jetzt aus useTodayEvents. Sie sind stabil
    // wie jeder useState-Setter, der Eintrag aendert also nichts.
  }, [manualRefreshTickets, manualRefreshEvents, setLinkedCalendarEventIds, setCalendarEventTicketStatus]);

  // dashboardEventRefresh: Ticket aus TicketDetail geschlossen, aus Mail/Teams
  // angelegt, im Heute-Kalender entplant. Der Heute-Kalender (DayCalendarPanel)
  // hört auf dasselbe Ereignis und lädt sich selbst neu.
  useEffect(() => {
    const handler = async () => {
      try {
        await aktualisiereNachAenderung();
      } catch (e) {
        console.error('Error handling dashboardEventRefresh:', e);
      }
    };
    window.addEventListener('dashboardEventRefresh', handler);
    return () => window.removeEventListener('dashboardEventRefresh', handler);
  }, [aktualisiereNachAenderung]);

  // Handle ticket click
  const handleTicketClick = useCallback(async (ticket) => {
    const currentUserEmail = getCurrentUserEmail();
    const isAssignedToCurrentUser = currentUserEmail && ticket.assignedTo &&
      ticket.assignedTo.localeCompare(currentUserEmail, undefined, { sensitivity: 'base' }) === 0;
    const isUnassigned = !ticket.assignedTo || ticket.assignedTo.trim() === '';

    // Optimistisch: Entferne die rote Ecke sofort
    if (isAssignedToCurrentUser) {
      setTickets(currentTickets => currentTickets.map(t => 
        t.id === ticket.id ? { ...t, hasNewEmails: false, hasNewComments: false, hasNewAttachments: false, isNewTicket: false, hasNewActivity: false, hasNewEmailsForAssignee: false, hasNewCommentsForAssignee: false, hasNewAttachmentsForAssignee: false, isNewTicketForAssignee: false, hasNewActivityForAssignee: false } : t
      ));
      // Also update in "Meine Tickets" list
      setMyTickets(currentTickets => currentTickets.map(t => 
        t.id === ticket.id ? { ...t, hasNewEmails: false, hasNewComments: false, hasNewAttachments: false, isNewTicket: false, hasNewActivity: false, hasNewEmailsForAssignee: false, hasNewCommentsForAssignee: false, hasNewAttachmentsForAssignee: false, isNewTicketForAssignee: false, hasNewActivityForAssignee: false } : t
      ));
    } else if (isUnassigned) {
      // Unassigned: global gelesen → entferne Indikatoren in beiden Listen
      setTickets(currentTickets => currentTickets.map(t => 
        t.id === ticket.id ? { ...t, hasNewEmails: false, hasNewComments: false, hasNewAttachments: false, isNewTicket: false, hasNewActivity: false, hasNewEmailsForAssignee: false, hasNewCommentsForAssignee: false, hasNewAttachmentsForAssignee: false, isNewTicketForAssignee: false, hasNewActivityForAssignee: false } : t
      ));
      setMyTickets(currentTickets => currentTickets.map(t => 
        t.id === ticket.id ? { ...t, hasNewEmails: false, hasNewComments: false, hasNewAttachments: false, isNewTicket: false, hasNewActivity: false, hasNewEmailsForAssignee: false, hasNewCommentsForAssignee: false, hasNewAttachmentsForAssignee: false, isNewTicketForAssignee: false, hasNewActivityForAssignee: false } : t
      ));
    }

    // Öffne das Modal sofort
    setSelectedTicket(ticket);
    setShowTicketDetail(true);
    
    // Mark ticket as viewed im Hintergrund
    try {
      if (isAssignedToCurrentUser || isUnassigned) {
        await apiService.markTicketAsViewed(ticket.id);
        // Sofortige Aktualisierung der Ticket-Liste für korrekte Sortierung
        await manualRefreshTickets();
      }
    } catch (error) {
      console.error('Error marking ticket as viewed:', error);
      
      // Falls der API-Call fehlschlägt, zeige die rote Ecke wieder an
      if (isAssignedToCurrentUser || isUnassigned) {
        setTickets(currentTickets => currentTickets.map(t => 
          t.id === ticket.id ? { ...t, hasNewActivity: true } : t
        ));
        setMyTickets(currentTickets => currentTickets.map(t => 
          t.id === ticket.id ? { ...t, hasNewActivity: true } : t
        ));
      }
    }
    // #529: Die beiden Setter kommen jetzt aus useDashboardTickets. Sie sind
    // stabil wie jeder useState-Setter, der Eintrag aendert also nichts.
  }, [manualRefreshTickets, getCurrentUserEmail, setTickets, setMyTickets]);

  // Event click handler: opens TicketDetail if a ticket object is passed; otherwise opens CalendarEventDetail for Graph event
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDetail, setShowEventDetail] = useState(false);
  const handleEventClick = useCallback((payload) => {
    // payload can be a ticket (object with id) or a Graph event (object with start/end/subject)
    if (payload && typeof payload === 'object' && payload.id != null && (payload.ticketNumber != null || payload.status != null || payload.title != null)) {
      // Heuristik: Ticket-Objekt
      setSelectedTicket(payload);
      setShowTicketDetail(true);
      return;
    }
    // Graph-Event
    setSelectedEvent(payload);
    setShowEventDetail(true);
  }, []);

  // #473: Schließen des TicketDetail-Modals. Die id kommt als Argument vom
  // Aufrufer. Vorher las die Funktion `selectedTicket` aus ihrer Closure, und
  // die stammte aus dem ersten Render (null) -- das sofortige Ausblenden eines
  // abgeschlossenen Tickets lief deshalb nie.
  const handleCloseTicketDetail = useCallback((hasChanges = false, ticketId = null) => {
    setShowTicketDetail(false);
    setSelectedTicket(null);

    if (!hasChanges) {
      console.log('📊 [DASHBOARD] Ticket was only viewed, no refresh needed');
      return;
    }

    if (ticketId != null) {
      const idStr = String(ticketId);
      if (hasChanges === 'completed') {
        // Sofort aus beiden Listen nehmen und kurz unterdrücken: Die Ticketliste
        // hat einen 2,5-s-SWR-Cache, eine Antwort von vor dem Abschließen würde
        // das Ticket sonst wieder einblenden (Muster wie bei 'planned').
        optimisticHiddenIdsRef.current.set(idStr, Date.now() + 30000);
        setMyTickets(prev => prev.filter(t => String(t.id) !== idStr));
        setTickets(prev => prev.filter(t => String(t.id) !== idStr));
      } else if (hasChanges === 'status_uncompleted') {
        // Wieder geöffnet: eine noch laufende Unterdrückung aufheben.
        optimisticHiddenIdsRef.current.delete(idStr);
      }
    }

    console.log(`📊 [DASHBOARD] Ticket closed with changes (${hasChanges}), refreshing data...`);
    // Genau eine Aktualisierung, und zwar über das Ereignis: So lädt der
    // Heute-Kalender mit, den Rest erledigt der eigene Listener oben.
    window.dispatchEvent(new Event('dashboardEventRefresh'));
    // #529: Setter und Ref kommen jetzt aus useDashboardTickets. Beide sind
    // stabil (useState-Setter, useRef), die Funktion bleibt also dieselbe.
  }, [optimisticHiddenIdsRef, setTickets, setMyTickets]);

  const handleTicketDataUpdate = useCallback(async () => {
    // Refresh tickets and events efficiently instead of full dashboard reload
    try {
      await aktualisiereNachAenderung();
    } catch (error) {
      console.error('Manual refresh error:', error);
      setError(t('dashboard.refreshError', 'Fehler beim Aktualisieren der Daten'));
    }
    
    // If we have a selected ticket, update it with the latest data
    if (selectedTicket) {
      try {
        const response = await apiService.getTickets();
        const updatedTicket = response.tickets.find(t => t.id === selectedTicket.id);
        if (updatedTicket) {
          setSelectedTicket(updatedTicket);
        }
      } catch (error) {
        console.error('Error updating selected ticket:', error);
      }
    }
  }, [aktualisiereNachAenderung, selectedTicket]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Loading debug: start tasks
      try {
        loadingDebug.start('dash-tickets', 'Dashboard: Tickets laden', 'local');
        loadingDebug.start('dash-emails', 'Dashboard: E-Mails laden', 'o365');
        loadingDebug.start('dash-events', 'Dashboard: Termine (heute) laden', 'o365');
        loadingDebug.start('dash-linked', 'Dashboard: Verknüpfte Elemente laden', 'local');
      } catch (_) {}
      // 1) Load tickets FIRST so UI can render quickly
      const ticketsResponse = await apiService.getTickets();
      const originalTicketsList = ticketsResponse.tickets || [];
      setOriginalTickets(originalTicketsList);
      const mine = filterTicketsAssignedToMe(originalTicketsList);
      setMyTickets(mine);
      const others = filterTicketsNotAssignedToMe(originalTicketsList);
      setTickets(others);
      try { loadingDebug.done('dash-tickets', true); } catch (_) {}

      // 2) Start E-Mails im Hintergrund (UI nicht blockieren)
      try { setEmailsLoading(true); } catch (_) {}
      try {
        manualRefreshEmails()
          .then(() => { try { loadingDebug.done('dash-emails', true); } catch (_) {} })
          .catch(() => { try { loadingDebug.done('dash-emails', false); } catch (_) {} })
          .finally(() => { try { setEmailsLoading(false); } catch (_) {} });
      } catch (_) {
        try { loadingDebug.done('dash-emails', false); } catch (_) {}
        try { setEmailsLoading(false); } catch (_) {}
      }

      // 3) Load events for today (explicit fetch to avoid Zwischenzustand)
      const now = new Date();
      const startRange = new Date(now);
      startRange.setDate(now.getDate() - 1);
      startRange.setHours(0, 0, 0, 0);
      const endRange = new Date(now);
      endRange.setDate(now.getDate() + 1);
      endRange.setHours(23, 59, 59, 999);
      try {
        const eventsResponse = await apiService.getEvents(startRange, endRange);
        setEvents(eventsResponse.value || []);
        try { loadingDebug.done('dash-events', true); } catch (_) {}
      } catch (e) {
        // ignore, auto-cache may still backfill
        try { loadingDebug.done('dash-events', false); } catch (_) {}
      }

      // 4) Load linked calendar items for ticket enrichment before render (scoped + timeout)
      try {
        const now2 = new Date();
        const start2 = new Date(now2);
        start2.setDate(now2.getDate() - 1);
        start2.setHours(0, 0, 0, 0);
        const end2 = new Date(now2);
        end2.setDate(now2.getDate() + 1);
        end2.setHours(23, 59, 59, 999);
        const linked = await apiService.getLinkedItems({ startDate: start2, endDate: end2, timeoutMs: 9000 });
        setLinkedCalendarEventIds(linked.linkedCalendarEventIds || []);
        setCalendarEventTicketStatus(linked.calendarEventTicketStatus || {});
        try { loadingDebug.done('dash-linked', true); } catch (_) {}
      } catch (_) {
        setLinkedCalendarEventIds([]);
        setCalendarEventTicketStatus({});
        try { loadingDebug.done('dash-linked', false); } catch (_) {}
      }

      // Trigger auto-cache refresh for events in background
      await manualRefreshEvents();

      // Erst jetzt Gesamt-Loading beenden, damit "Heutige Termine" gefiltert vorliegt
      setLoading(false);
      try {
        // Auto-hide debug entries shortly after success/failure
        setTimeout(() => { try { loadingDebug.clearAll(); } catch (_) {} }, 3000);
      } catch (_) {}

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      if (error.response?.status === 401) {
        setError(t('common.sessionExpired', 'Sitzung abgelaufen. Bitte melden Sie sich erneut an.'));
        setTimeout(() => {
          try {
            // Gate: nur Auto-SSO, wenn Cookie vorhanden
            const raw = String(document.cookie || '');
            const allowAutoSSO = raw.split(';').some(c => c.trim().startsWith('was_logged_in=1'));
            window.location.href = allowAutoSSO ? '/auth/sso' : '/auth/login';
          } catch (_) {
            window.location.href = '/auth/login';
          }
        }, 3000);
      } else {
        setError(t('dashboard.errorLoadData', 'Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut.'));
      }
    }
  };


  

  // Service status component
  const ServiceStatusBanner = () => {
    const hasSuspendedServices = emailsSuspended || eventsSuspended || ticketsSuspended;
    
    if (!hasSuspendedServices) {
      return null;
    }

    const suspendedServices = [];
    if (emailsSuspended) suspendedServices.push('E-Mails');
    if (eventsSuspended) suspendedServices.push('Termine');
    if (ticketsSuspended) suspendedServices.push('Tickets');

    const handleRetryServices = async () => {
      setLoading(true);
      try {
        console.log('🔄 Manual retry of suspended services...');
        // E-Mails im Hintergrund neu versuchen
        try {
          setEmailsLoading(true);
          manualRefreshEmails()
            .catch(() => {})
            .finally(() => { try { setEmailsLoading(false); } catch (_) {} });
        } catch (_) {}
        await Promise.all([
          manualRefreshEvents(), 
          manualRefreshTickets()
        ]);
      } catch (error) {
        console.error('Error retrying services:', error);
        setError('Fehler beim Wiederverbinden der Services');
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="service-status-banner" style={{
        backgroundColor: 'var(--color-warning-bg)',
        border: '1px solid var(--color-warning-border)',
        borderRadius: '8px',
        padding: '12px 16px',
        margin: '0 0 20px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <div>
            <strong>{t('dashboard.serviceIssues', 'Service-Verbindungsprobleme')}</strong>
            <div style={{ fontSize: '0.9rem', color: 'var(--color-warning-text-dark)' }}>
              Automatische Aktualisierung pausiert für: {suspendedServices.join(', ')}
              <br />
              Ihre Sitzung ist möglicherweise abgelaufen.
            </div>
          </div>
        </div>
        <button 
          onClick={handleRetryServices}
          disabled={loading}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            whiteSpace: 'nowrap'
          }}
        >
          {loading ? t('dashboard.connecting', 'Verbinde...') : t('dashboard.retry', 'Erneut versuchen')}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <RefreshCw className="animate-spin" size={32} />
          <p>{t('dashboard.loading', 'Lade Daten...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // #563: Bereiche aus dem Register (dashboard/bereiche.js). Das Register
  // bestimmt Reihenfolge und Sichtbarkeit, hier steht, was ein Bereich zeigt --
  // woertlich aus dem bisherigen JSX, weil es am Zustand des Dashboards haengt.
  // #569: Reihenfolge und ausgeblendete Bereiche aus den Benutzer-Einstellungen
  const bereichsZustand = { siehtPool, ausgeblendet, reihenfolge, showTodayCalendar };
  const bereiche = sichtbareBereiche(bereichsZustand);
  const poolSichtbar = bereiche.some((b) => b.id === 'pool');
  const meineSichtbar = bereiche.some((b) => b.id === 'meine');
  // Ausgeblendet, aber für die Rolle da: Mails und Teams haben ihre eigenen
  // Knöpfe in der Leiste unten, die übrigen bekommen einen allgemeinen
  const versteckteBereiche = erlaubteBereiche(bereichsZustand).filter((b) => ausgeblendet.includes(b.id) && b.id !== 'mails' && b.id !== 'teams');
  const neuesTicketKnopf = (
            <button
              onClick={() => { setPendingCreateTicketFiles([]); setShowCreateTicketForm(true); }}
              className="btn btn-primary btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem', padding: '6px 12px', justifyContent: 'center' }}
              title={t('calendar.newTicket', 'Neues Ticket erstellen')}
            >
              <Plus size={16} viewBox="0 0 16 25" />
              {t('dashboard.tickets', 'Tickets')}
            </button>
  );
  const darstellung = {
    heute: (platz) => (platz === 'band' ? (
        <div className="dashboard-section compact-events-section">
          <div className="section-header">
            <Calendar size={20} />
            <h2>{t('dashboard.todayEvents', 'Heutige Termine')}</h2>
            {eventErrorCount > 0 && (
              <span style={{ 
                fontSize: '12px', 
                color: eventsSuspended ? 'var(--color-danger)' : 'var(--color-warning)',
                marginLeft: '8px',
                fontWeight: 'normal'
              }}>
                {eventsSuspended ? t('common.paused', '⚠️ Pausiert') : t('common.errors', `⚠️ ${eventErrorCount} Fehler`, { count: eventErrorCount })}
              </span>
            )}
            <button
              onClick={handleActivateTodayCalendar}
              className="btn btn-secondary btn-sm icon-only"
              title={t('dashboard.showTodayCalendar', 'Kalenderansicht anzeigen')}
              style={{ padding: '6px', marginLeft: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              aria-label={t('dashboard.expandToCalendar', 'Zur Kalenderansicht wechseln')}
            >
              <ChevronDown size={16} />
            </button>
          </div>
          <CompactEventList 
            events={events} 
            onEventClick={handleEventClick} 
            refreshKey={eventRefreshKey}
            currentUserEmail={(typeof currentUser === 'string' ? currentUser : (currentUser?.userPrincipalName || currentUser?.mail || currentUser?.email || null))}
            linkedCalendarEventIds={linkedCalendarEventIds}
            calendarEventTicketStatus={calendarEventTicketStatus}
            plannedTicketEntries={plannedTicketEntries}
          />
        </div>
    ) : (
        <div className="dashboard-section today-calendar-section">
          <div className="section-header">
            <Calendar size={20} />
            <h2>{t('dashboard.todayLabel', 'Heute')}</h2>
            <button
              onClick={handleDeactivateTodayCalendar}
              className="btn btn-secondary btn-sm icon-only"
              title={t('dashboard.hideTodayCalendar', 'Kalenderansicht ausblenden')}
              style={{ padding: '6px', marginLeft: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              aria-label={t('dashboard.collapseCalendar', 'Zur Liste Heutige Termine wechseln')}
            >
              <ChevronUp size={16} />
            </button>
          </div>
          <div
            ref={todayCalendarWrapRef}
            style={{
              height: todayCalendarHeight || 520,
              minHeight: 520,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <DayCalendarPanel
              isEmbedded={true}
              date={todayDate}
              currentUser={currentUser}
              onOpenTicket={(ticket) => { try { if (ticket && ticket.id) { handleTicketClick(ticket); } } catch(_) {} }}
              onOpenEvent={(event) => { try { setSelectedEvent(event); setShowEventDetail(true); } catch(_) {} }}
              linkedCalendarEventIds={linkedCalendarEventIds}
              calendarEventTicketStatus={calendarEventTicketStatus}
            />
          </div>
        </div>
    )),
    meine: () => (
      <div className="dashboard-section">
        <div className="section-header section-header-with-search">
          <Ticket size={20} />
          <h2>{t('dashboard.myTickets', 'Meine')}</h2>
          {ticketErrorCount > 0 && (
            <span style={{ 
              fontSize: '12px', 
              color: ticketsSuspended ? 'var(--color-danger)' : 'var(--color-warning)',
              marginLeft: '8px',
              fontWeight: 'normal'
            }}>
              {ticketsSuspended ? t('common.paused', '⚠️ Pausiert') : t('common.errors', `⚠️ ${ticketErrorCount} Fehler`, { count: ticketErrorCount })}
            </span>
          )}
          <div className="ticket-search-container">
            <div className="search-input-wrapper">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder={t('dashboard.searchTicketsPlaceholder', 'Tickets durchsuchen...')}
                value={myTicketSearchFilter}
                onChange={(e) => setMyTicketSearchFilter(e.target.value)}
                className="ticket-search-input"
              />
            </div>
          </div>
          <button 
            onClick={() => setShowTicketFilterModal(true)}
            className="btn btn-secondary btn-sm icon-only"
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0px',
              fontSize: '0.875rem',
              padding: '6px',
              justifyContent: 'center'
            }}
            title={t('dashboard.configureTicketFilter', 'Ticket Filter konfigurieren')} aria-label={t('dashboard.configureTicketFilter', 'Ticket Filter konfigurieren')}
          >
            <Filter size={16} viewBox="0 0 16 25" />
          </button>
          {/* "+ Ticket" sitzt in der Pool-Spalte; ist der Pool nicht sichtbar (Rolle oder eigene Wahl, #569), sitzt er hier (#563) */}
          {!poolSichtbar && neuesTicketKnopf}
        </div>
        <DashboardTicketList 
          tickets={myTickets} 
          onTicketClick={handleTicketClick} 
          onTicketUpdate={handleTicketDataUpdate}
          searchFilter={myTicketSearchFilter}
          useAssigneePerspective={true}
          status={ticketsStatus}
          onRetry={manualRefreshTickets}
        />
      </div>
    ),
    pool: () => (
      <div className="dashboard-section tickets-section" ref={ticketsSectionRef}>
        <div className="section-header section-header-with-search" ref={ticketsHeaderRef}>
          <Ticket size={20} />
          <h2>Pool</h2>
          {ticketErrorCount > 0 && (
            <span style={{ 
              fontSize: '12px', 
              color: ticketsSuspended ? 'var(--color-danger)' : 'var(--color-warning)',
              marginLeft: '8px',
              fontWeight: 'normal'
            }}>
              {ticketsSuspended ? t('common.paused', '⚠️ Pausiert') : t('common.errors', `⚠️ ${ticketErrorCount} Fehler`, { count: ticketErrorCount })}
            </span>
          )}
          <div className="ticket-search-container">
            <div className="search-input-wrapper">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder={t('dashboard.searchTicketsPlaceholder', 'Tickets durchsuchen...')}
                value={ticketSearchFilter}
                onChange={(e) => setTicketSearchFilter(e.target.value)}
                className="ticket-search-input"
              />
            </div>
          </div>
          <button 
            onClick={() => setShowTicketFilterModal(true)}
            className="btn btn-secondary btn-sm icon-only"
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0px',
              fontSize: '0.875rem',
              padding: '6px',
              justifyContent: 'center'
            }}
            title="Ticket Filter konfigurieren" aria-label="Ticket Filter konfigurieren"
          >
            <Filter size={16} viewBox="0 0 16 25" />
          </button>
          {/* W6 (#16): Pool als große Monitor-Ansicht öffnen (/pool) */}
          <button
            onClick={() => navigate('/pool')}
            className="btn btn-secondary btn-sm icon-only"
            style={{ padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title={t('dashboard.expandPool', 'Pool als Monitor-Ansicht öffnen')} aria-label={t('dashboard.expandPool', 'Pool als Monitor-Ansicht öffnen')}
          >
            <Maximize2 size={16} viewBox="0 0 16 25" />
          </button>
          <button
            onClick={() => { setPendingCreateTicketFiles([]); setShowCreateTicketForm(true); }}
            className={`btn btn-primary btn-sm ${isTicketsNarrow ? 'icon-only' : ''}`}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isTicketsNarrow ? '0px' : '4px',
              fontSize: '0.875rem',
              padding: isTicketsNarrow ? '6px' : '6px 12px',
              justifyContent: 'center'
            }}
            title={t('calendar.newTicket', 'Neues Ticket erstellen')}
          >
            <Plus size={16} viewBox="0 0 16 25" />
            {!isTicketsNarrow && t('dashboard.tickets', 'Tickets')}
          </button>
        </div>
        <DashboardTicketList 
          tickets={tickets} 
          onTicketClick={handleTicketClick} 
          onTicketUpdate={handleTicketDataUpdate}
          searchFilter={ticketSearchFilter}
          status={ticketsStatus}
          onRetry={manualRefreshTickets}
        />
      </div>
    ),
    mails: () => (
      <div className="dashboard-section">
        <div className="section-header section-header-with-search">
          <Mail size={20} />
          <h2>{t('dashboard.emails', 'Mails')}</h2>
          {emailErrorCount > 0 && (
            <span style={{ 
              fontSize: '12px', 
              color: emailsSuspended ? 'var(--color-danger)' : 'var(--color-warning)',
              marginLeft: '8px',
              fontWeight: 'normal'
            }}>
              {emailsSuspended ? t('common.paused', '⚠️ Pausiert') : t('common.errors', `⚠️ ${emailErrorCount} Fehler`, { count: emailErrorCount })}
            </span>
          )}
          <div className="ticket-search-container">
            <div className="search-input-wrapper">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder={t('dashboard.searchMailsPlaceholder', 'Mails durchsuchen...')}
                value={emailSearchFilter}
                onChange={(e) => setEmailSearchFilter(e.target.value)}
                className="ticket-search-input"
              />
            </div>
          </div>
          <>
            <button 
              onClick={() => setShowEmailFilterModal(true)}
              className="btn btn-secondary btn-sm icon-only"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0px',
                fontSize: '0.875rem',
                padding: '6px',
                justifyContent: 'center'
              }}
              title={t('dashboard.configureEmailSection', 'E-Mail Sektion konfigurieren')} aria-label={t('dashboard.configureEmailSection', 'E-Mail Sektion konfigurieren')}
            >
              <Cog size={16} viewBox="0 0 16 25" />
            </button>
            <button
              onClick={() => { setHideEmails(true); saveHiddenColumns({ emails: true, teams: hideTeams }); }}
              className="btn btn-secondary btn-sm icon-only"
              style={{ padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title={t('dashboard.hideEmails', 'E-Mails ausblenden')} aria-label={t('dashboard.hideEmails', 'E-Mails ausblenden')}
            >
              <EyeOff size={16} viewBox="0 0 16 25" />
            </button>
          </>
        </div>
        {/* Zeige Suchergebnisse, falls Suchfeld gefüllt ist */}
        {emailSearchFilter.trim() ? (
          <>
            {emailSearchLoading && (
              <div className="loading" style={{ padding: '8px 0' }}>
                <RefreshCw className="animate-spin" size={18} />
                <p style={{ marginLeft: 8 }}>{t('dashboard.searching', 'Suche…')}</p>
              </div>
            )}
            <EmailList 
              emails={emailSearchResults} 
              onEmailUpdate={handleEmailReadUpdate} 
              filterSettings={{}}
              maxDisplayEmails={null}
              disableBackgroundAutoAssign={true}
              compact={true}
            />
          </>
        ) : (
          emailsLoading ? (
            <div className="loading" style={{ padding: '8px 0' }}>
              <RefreshCw className="animate-spin" size={18} />
              <p style={{ marginLeft: 8 }}>{t('dashboard.loading', 'Lade Daten...')}</p>
            </div>
          ) : (
            <EmailList 
              emails={emails} 
              onEmailUpdate={handleEmailReadUpdate} 
              filterSettings={emailFilterSettings}
              maxDisplayEmails={30}
              disableBackgroundAutoAssign={Boolean(emailSearchFilter && emailSearchFilter.trim())}
              compact={true}
              status={emailsStatus}
              onRetry={manualRefreshEmails}
            />
          )
        )}
      </div>
    ),
    teams: () => (
      <div className="dashboard-section">
        <div className="section-header">
          <MessageCircle size={20} />
          <h2>{t('dashboard.teams', 'Teams')}</h2>
          <>
            <button 
              onClick={() => setShowTeamsFilterModal(true)}
              className="btn btn-secondary btn-sm icon-only"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0px',
                fontSize: '0.875rem',
                padding: '6px',
                justifyContent: 'center'
              }}
              title={t('dashboard.configureTeamsFilter', 'Teams-Nachrichten Filter konfigurieren')} aria-label={t('dashboard.configureTeamsFilter', 'Teams-Nachrichten Filter konfigurieren')}
            >
              <Filter size={16} viewBox="0 0 16 25" />
            </button>
            <button
              onClick={() => { setHideTeams(true); saveHiddenColumns({ emails: hideEmails, teams: true }); }}
              className="btn btn-secondary btn-sm icon-only"
              style={{ padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title={t('dashboard.hideTeams', 'Teams ausblenden')} aria-label={t('dashboard.hideTeams', 'Teams ausblenden')}
            >
              <EyeOff size={16} viewBox="0 0 16 25" />
            </button>
          </>
        </div>
        <TeamsMessageList limit={30} unreadOnly={false} onMessageCountUpdate={handleTeamsMessageCountUpdate} filterSettings={teamsFilterSettings} />
      </div>
    )
  };

  return (
    <div className="container">
      <div className="dashboard">
        <div className="dashboard-container">
          <ServiceStatusBanner />
          
          {bereiche.filter((b) => b.platz === 'band').map((b) => (
            <React.Fragment key={b.id}>{darstellung[b.id](b.platz)}</React.Fragment>
          ))}

          <div className="dashboard-grid">
            {bereiche.filter((b) => b.platz === 'raster').map((b) => (
              <React.Fragment key={b.id}>{darstellung[b.id](b.platz)}</React.Fragment>
            ))}
          </div>

          {/* #569: alles ausgeblendet -- Hinweis statt leerer Seite */}
          {bereiche.length === 0 && (
            <div className="dashboard-section dashboard-leer">
              <p>{t('dashboard.alleAusgeblendet', 'Alle Bereiche des Dashboards sind ausgeblendet.')}</p>
              <button type="button" className="btn btn-secondary" onClick={() => window.dispatchEvent(new CustomEvent('einstellungenOeffnen'))}>
                <Cog size={16} aria-hidden="true" />
                {t('dashboard.einstellungenOeffnen', 'Einstellungen öffnen')}
              </button>
            </div>
          )}

          
        </div>
      </div>
      {(hideEmails || hideTeams || versteckteBereiche.length > 0) && (
        <div style={{
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border-light)',
          borderRadius: '8px',
          padding: '10px 12px',
          margin: '16px 0 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>{t('dashboard.hiddenSectionsLabel', 'Ausgeblendete Bereiche:')}</span>
          {hideEmails && (
            <button
              onClick={() => { setHideEmails(false); saveHiddenColumns({ emails: false, teams: hideTeams }); }}
              className="btn btn-secondary btn-sm icon-only"
              title={t('dashboard.showEmails', 'E-Mails einblenden')}
              style={{ padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Eye size={16} viewBox="0 0 16 25" />
              <span style={{ marginLeft: 6 }}>{t('dashboard.emails', 'E-Mails')}</span>
            </button>
          )}
          {hideTeams && (
            <button
              onClick={() => { setHideTeams(false); saveHiddenColumns({ emails: hideEmails, teams: false }); }}
              className="btn btn-secondary btn-sm icon-only"
              title={t('dashboard.showTeams', 'Teams einblenden')}
              style={{ padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Eye size={16} viewBox="0 0 16 25" />
              <span style={{ marginLeft: 6 }}>{t('dashboard.teams', 'Teams')}</span>
            </button>
          )}
          {/* #569: alle übrigen ausgeblendeten Bereiche */}
          {versteckteBereiche.map((b) => {
            const name = t(b.titelKey, b.titel);
            return (
              <button
                key={b.id}
                onClick={() => blendeBereich(b.id, false)}
                className="btn btn-secondary btn-sm icon-only"
                title={t('dashboard.showBereich', '{{name}} einblenden', { name })}
                style={{ padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Eye size={16} viewBox="0 0 16 25" />
                <span style={{ marginLeft: 6 }}>{name}</span>
              </button>
            );
          })}
          {/* #569: ohne Pool und ohne Meine wandert "+ Ticket" hierher */}
          {!poolSichtbar && !meineSichtbar && <span style={{ marginLeft: 'auto' }}>{neuesTicketKnopf}</span>}
        </div>
      )}

      {/* TICKET DETAIL MODAL */}
      {showTicketDetail && selectedTicket && (
        <TicketDetail 
          isMobileView={isMobileScreen}
          ticket={selectedTicket}
          onClose={(hasChanges = false) => handleCloseTicketDetail(hasChanges, selectedTicket?.id)}
          onTicketUpdate={handleTicketDataUpdate}
        />
      )}

      {/* EVENT DETAIL MODAL */}
      {showEventDetail && selectedEvent && (
        <CalendarEventDetail 
          event={selectedEvent}
          onClose={() => { setShowEventDetail(false); setSelectedEvent(null); }}
        />
      )}

      {/* CREATE TICKET MODAL (#528: components/dashboard/CreateTicket.js) */}
      <CreateTicketModal anlegen={anlegen} />

      {/* EMAIL FILTER MODAL */}
      <EmailFilterModal
        isOpen={showEmailFilterModal}
        onClose={() => setShowEmailFilterModal(false)}
        onSave={handleEmailFilterSave}
        currentSettings={emailFilterSettings}
        loading={emailFilterLoading}
      />

      {/* TEAMS FILTER MODAL */}
      <TeamsFilterModal
        isOpen={showTeamsFilterModal}
        onClose={() => setShowTeamsFilterModal(false)}
        onSave={handleTeamsFilterSave}
        currentSettings={teamsFilterSettings}
        loading={teamsFilterLoading}
      />

      {/* TICKET FILTER MODAL */}
      <TicketFilterModal
        isOpen={showTicketFilterModal}
        onClose={() => setShowTicketFilterModal(false)}
        onSave={handleTicketFilterSave}
        currentSettings={ticketFilterSettings}
        loading={ticketFilterLoading}
      />
      
      {/* Release Note Overlay */}
      {showReleaseNote && releaseNote && (
        <ReleaseNoteOverlay releaseNote={releaseNote} onClose={handleCloseReleaseNote} />
      )}

      {/* Debug Tool - nur für Testing */}
      {(process.env.NODE_ENV === 'development' || window.location.search.includes('debug=true')) && (
        <TokenDebug />
      )}
    </div>
  );
};

export default Dashboard; 